import { Container } from '@pixi/display';
import { Signal } from 'typed-signals';
import { getView } from './utils/helpers/view.mjs';

class Switcher extends Container {
  /**
   * @param {Array<Container | string>} views - Array of views or textures that will be switching.
   * @param triggerEvents - Button events, to switch views (can be one event or an array of events).
   * @param activeViewID - The id of the view, visible by default.
   */
  constructor(views, triggerEvents, activeViewID) {
    super();
    this._triggerEvents = /* @__PURE__ */ new Set(["onPress"]);
    this.innerView = new Container();
    this.addChild(this.innerView);
    this.onChange = new Signal();
    if (views)
      this.views = views;
    if (triggerEvents)
      this.triggerEvents = triggerEvents;
    if (activeViewID && this.views.length > 0)
      this.active = activeViewID;
    this.setInteractionEvents();
  }
  setInteractionEvents() {
    this.innerView.eventMode = "static";
    this.innerView.on("pointerdown", () => this.handleEvents("onDown"));
    this.innerView.on("pointerup", () => this.handleEvents("onUp"));
    this.innerView.on("pointerupoutside", () => this.handleEvents("onUpOut"));
    this.innerView.on("pointerout", () => this.handleEvents("onOut"));
    this.innerView.on("pointertap", () => this.handleEvents("onPress"));
    this.innerView.on("pointerover", () => this.handleEvents("onHover"));
  }
  handleEvents(event) {
    if (this._triggerEvents.has(event)) {
      this.switch();
    }
  }
  /** Returns the active view. */
  get activeView() {
    if (this.views && this.views[this.active]) {
      return this.views[this.active];
    }
    return void 0;
  }
  /** Sets the list of instances for switching. */
  set views(views) {
    this.innerView.removeChildren();
    views.forEach((stateView) => this.add(stateView));
  }
  /** Returns all the switchable views */
  get views() {
    return this.innerView.children;
  }
  /**
   * Adds view instance to a switching list.
   * @param view
   */
  add(view) {
    const viewInstance = getView(view);
    this.innerView.addChild(viewInstance);
    viewInstance.visible = false;
    if (this.views.length === 1) {
      this.active = 0;
    }
  }
  /**
   * Removes view instance from a switching list by id.
   * @param id - id of the view to remove.
   */
  remove(id) {
    if (this.views[id]) {
      this.innerView.removeChild(this.views[id]);
    }
  }
  /**
   * Sets a list of events that will make a switcher switch to the next view.
   * @param {ButtonEvent | ButtonEvent[]} triggerEvents - Button events,
   * to switch views (can be one event or an array of events).
   */
  set triggerEvents(triggerEvents) {
    this._triggerEvents = new Set(Array.isArray(triggerEvents) ? triggerEvents : [triggerEvents]);
  }
  /** Returns a list of events that will make a switcher switch to the next view. */
  get triggerEvents() {
    return Array.from(this._triggerEvents);
  }
  /**
   * Show a view by id, or to next one by order, if no ID provided.
   * @param {number} id - optional id of the view to show. If not set, will switch to the next view.
   */
  switch(id) {
    if (id !== void 0 && id === this.active)
      return;
    const exID = this.active;
    this.forceSwitch(id);
    if (exID !== this.active) {
      const res = this.views.length > 2 ? this.active : this.active === 1;
      this.onChange.emit(res);
    }
  }
  /**
   * Switches a view to a given one without triggering the onChange event.
   * @param {number} id - optional id of the view to show. If not set, will switch to the next view.
   */
  forceSwitch(id) {
    if (id !== void 0 && id === this.active)
      return;
    if (this.activeView) {
      this.activeView.visible = false;
    }
    if (id !== void 0 && !this.views[id]) {
      throw new Error(`View with id ${id} does not exist.`);
    }
    this._active = id !== void 0 ? id : this.nextActive;
    if (this._active === void 0) {
      return;
    }
    this.views[this.active].visible = true;
  }
  /** Returns the id of the next view in order. Or undefined, if order is empty. */
  get nextActive() {
    if (this.views.length === 0)
      return void 0;
    return this.active < this.views.length - 1 ? this.active + 1 : 0;
  }
  /** Sets the id of the visible(active) view and shows to it. */
  set active(id) {
    this.switch(id);
  }
  /** Gets the id of the visible(active) view. */
  get active() {
    return this._active;
  }
}

export { Switcher };
//# sourceMappingURL=Switcher.mjs.map
